import Checkbox from "PFComponents/checkbox/checkbox";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { EditAccountForm, FormErrors, OwnerErrors } from "../../edit_account.types";
import css from "../edit_account_page.module.scss";

type BaseSectionProps = {
  formErrors: FormErrors | null;
};

export const BaseSection = ({ formErrors }: BaseSectionProps): JSX.Element => {
  const { control } = useFormContext<EditAccountForm>();
  const { t } = useTranslation("saasAdmin");

  return (
    <div className={css.sectionContainer}>
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.name")}
            error={formErrors?.name}
          />
        )}
      />
      <Controller
        control={control}
        name="fullDomain"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.fullDomain")}
            error={formErrors?.full_domain}
          />
        )}
      />
      <Controller
        control={control}
        name="customDomain"
        render={({ field: { onChange, value } }) => (
          <Checkbox
            checked={value ?? false}
            handleChange={(val) => onChange(val)}
            label={t("accounts.account.edit.customDomain")}
          />
        )}
      />
      <Controller
        control={control}
        name="fromEmailDomain"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.fromEmailDomain")}
            error={formErrors?.from_email_domain}
          />
        )}
      />
      <Controller
        control={control}
        name="theme"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.theme")}
            error={formErrors?.theme}
          />
        )}
      />

      <Controller
        control={control}
        name="feedbackEmailRecipient"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.feedbackEmailRecipient")}
            error={formErrors?.feedback_email_recipient}
            inputType="email"
            multiple
          />
        )}
      />
      <Controller
        control={control}
        name="senderName"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.senderName")}
            error={formErrors?.sender_name}
          />
        )}
      />
      <Controller
        control={control}
        name="sessionTtlInMinutes"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.sessionTtlInMinutes")}
            error={formErrors?.session_ttl_in_minutes}
          />
        )}
      />
      <Controller
        control={control}
        name="liveTtlInMinutes"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value ?? ""}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.liveTtlInMinutes")}
            error={formErrors?.live_ttl_in_minutes}
          />
        )}
      />
      <Controller
        control={control}
        name="real"
        render={({ field: { onChange, value } }) => (
          <Checkbox
            checked={value ?? false}
            handleChange={(val) => onChange(val)}
            label={t("accounts.account.edit.real")}
          />
        )}
      />
      <Controller
        control={control}
        name="owner.name"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.ownerName")}
            error={(formErrors?.owner as OwnerErrors)?.name}
          />
        )}
      />
      <Controller
        control={control}
        name="owner.email"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.ownerEmail")}
            error={(formErrors?.owner as OwnerErrors)?.email}
          />
        )}
      />
      <Controller
        control={control}
        name="owner.password"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.ownerPassword")}
            error={(formErrors?.owner as OwnerErrors)?.password}
            inputType="password"
          />
        )}
      />
      <Controller
        control={control}
        name="owner.passwordConfirmation"
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldSet
            value={value}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            label={t("accounts.account.edit.ownerPasswordConfirmation")}
            error={(formErrors?.owner as OwnerErrors)?.password_confirmation}
            inputType="password"
          />
        )}
      />
    </div>
  );
};
